import React, { useEffect, useRef, useState } from "react";

/**
 * LazyImage — mounts the actual <img src> only when the element enters the viewport.
 * Uses IntersectionObserver + skeleton placeholder. Any URL is fine (relative /api/... paths
 * benefit the most since the browser can cache them; base64 works too but bypasses cache).
 *
 * Props:
 *   src        – image URL (relative or absolute)
 *   alt        – alt text
 *   className  – classes for both the container & the loaded img
 *   fallback   – React node shown when src is empty/missing
 *   rootMargin – IntersectionObserver rootMargin (default: 200px so images preload just before scroll)
 *   eager      – if true, load immediately (skip IntersectionObserver)
 */
export default function LazyImage({
  src,
  alt = "",
  className = "",
  fallback = null,
  rootMargin = "200px",
  eager = false,
  imgTestid,
}) {
  const wrapperRef = useRef(null);
  const [visible, setVisible] = useState(eager);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (eager || visible) return;
    if (!wrapperRef.current || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { setVisible(true); io.disconnect(); }
      });
    }, { rootMargin });
    io.observe(wrapperRef.current);
    return () => io.disconnect();
  }, [eager, visible, rootMargin]);

  // Reset load state when src changes (e.g., handle switched in CustomerBoxes)
  useEffect(() => { setLoaded(false); setFailed(false); }, [src]);

  if (!src) return fallback;

  return (
    <div ref={wrapperRef} className={`relative overflow-hidden ${className}`}>
      {(!loaded || !visible) && !failed && (
        <div className="absolute inset-0 bg-brand-cream animate-pulse" aria-hidden="true" />
      )}
      {visible && !failed && (
        <img
          data-testid={imgTestid}
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={`w-full h-full object-cover transition-opacity duration-200 ${loaded ? "opacity-100" : "opacity-0"}`}
        />
      )}
      {failed && (fallback || <div className="absolute inset-0 bg-brand-cream" />)}
    </div>
  );
}
